import React, { useState } from 'react';
import { FaChevronDown } from 'react-icons/fa';
import DateFormatter from 'components/DateFormatter';
import { currencyToLocal } from 'utils/constants';
import './mobile.css';

const getStatusClass = (value) => {
  const normalizedValue = `${value || ''}`.toLowerCase();

  if (
    normalizedValue.includes('active') &&
    !normalizedValue.includes('inactive')
  ) {
    return 'success';
  }

  if (normalizedValue.includes('inactive')) {
    return 'danger';
  }

  return 'muted';
};

const formatAmount = (plan, key) =>
  currencyToLocal(plan?.[key], plan?.currency);

const SubscriptionPlanMobileList = ({
  plans,
  totalPages,
  currentPageNo,
  pageSize,
  handlePageChange,
  renderActions,
  renderStatus,
}) => {
  const [expandedCardId, setExpandedCardId] = useState(null);
  const [activeMobileTabs, setActiveMobileTabs] = useState({});
  const tabs = ['Overview', 'Dates'];

  const toggleMobileCard = (planId) => {
    setExpandedCardId((prev) => (prev === planId ? null : planId));
  };

  const handleMobileTabChange = (planId, tab) => {
    setActiveMobileTabs((prev) => ({
      ...prev,
      [planId]: tab,
    }));
  };

  const getSections = (plan) => ({
    Overview: [
      { label: 'Amount', value: formatAmount(plan, 'price') },
      { label: 'Discount', value: formatAmount(plan, 'discountedAmount') },
      { label: 'Discount %', value: `${plan?.discountedPercentage ?? 0}%` },
      { label: 'Duration', value: `${plan?.duration || 0} days` },
      { label: 'Status', value: renderStatus(plan), control: true },
    ],
    Dates: [
      { label: 'Created', value: <DateFormatter dateTime={plan?.createdAt} /> },
      {
        label: 'Modified',
        value: <DateFormatter dateTime={plan?.updatedAt} />,
      },
      { label: 'Currency', value: plan?.currency || 'N/A' },
    ],
  });

  return (
    <>
      <div className="subscription-mobile-list">
        {(plans || []).map((plan, index) => {
          const activeTab = activeMobileTabs[plan.id] || tabs[0];
          const sections = getSections(plan);
          const activeSection = sections[activeTab] || [];
          const status = plan?.status || 'N/A';
          const isExpanded = expandedCardId === plan.id;

          return (
            <div className="subscription-mobile-card" key={plan.id}>
              <div className="subscription-mobile-card__summary">
                <div className="subscription-mobile-card__identity">
                  <div className="subscription-mobile-card__name">
                    {plan?.planName || 'N/A'}
                  </div>
                  <div className="subscription-mobile-card__meta">
                    {`${formatAmount(plan, 'price')} | ${plan?.duration || 0} days`}
                  </div>
                </div>
                <div className="subscription-mobile-card__aside">
                  <span
                    className={`subscription-mobile-card__pill ${getStatusClass(status)}`}
                  >
                    {status}
                  </span>
                  <div className="subscription-mobile-card__tools">
                    <button
                      type="button"
                      className={`subscription-mobile-card__expand-toggle ${isExpanded ? 'is-open' : ''}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        toggleMobileCard(plan.id);
                      }}
                      aria-expanded={isExpanded}
                      aria-label={
                        isExpanded
                          ? 'Collapse plan details'
                          : 'Expand plan details'
                      }
                    >
                      <FaChevronDown />
                    </button>
                    {renderActions ? renderActions(plan, index) : null}
                  </div>
                </div>
              </div>

              {isExpanded && (
                <div className="subscription-mobile-card__body">
                  <div className="subscription-mobile-card__tabs">
                    {tabs.map((tab) => (
                      <button
                        key={tab}
                        type="button"
                        className={`subscription-mobile-card__tab ${activeTab === tab ? 'active' : ''}`}
                        onClick={() => handleMobileTabChange(plan.id, tab)}
                      >
                        {tab}
                      </button>
                    ))}
                  </div>

                  <div className="subscription-mobile-card__detail-list">
                    {activeSection.map((detail) => (
                      <div
                        className="subscription-mobile-card__detail-row"
                        key={detail.label}
                      >
                        <div className="subscription-mobile-card__detail-label">
                          {detail.label}
                        </div>
                        <div className="subscription-mobile-card__detail-value">
                          {detail.control ? (
                            <div className="subscription-mobile-card__control-wrap">
                              {detail.value}
                            </div>
                          ) : (
                            detail.value
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {totalPages ? (
        <div className="subscription-mobile-pagination">
          <span className="subscription-mobile-pagination__count">
            {currentPageNo} of {totalPages}
          </span>
          <select
            className="page-select form-select table-select f-12 fw-normal subscription-mobile-pagination__select"
            value={pageSize}
            onChange={(e) => {
              const nextPageSize = Number(e.target.value);
              handlePageChange({
                pageNo: totalPages === currentPageNo ? 1 : currentPageNo,
                pageSize: nextPageSize,
              });
            }}
          >
            {[10, 20, 30, 40, 50].map((size) => (
              <option key={size} value={size}>
                {size} per page
              </option>
            ))}
          </select>
          <div className="pagination subscription-mobile-pagination__actions">
            <button
              type="button"
              onClick={() => handlePageChange({ pageNo: 1, pageSize })}
              disabled={currentPageNo <= 1}
            >
              {'<<'}
            </button>
            <button
              type="button"
              onClick={() =>
                handlePageChange({ pageNo: currentPageNo - 1, pageSize })
              }
              disabled={currentPageNo === 1}
            >
              {'<'}
            </button>
            <button
              type="button"
              onClick={() =>
                handlePageChange({ pageNo: currentPageNo + 1, pageSize })
              }
              disabled={currentPageNo >= totalPages}
            >
              {'>'}
            </button>
            <button
              type="button"
              onClick={() => handlePageChange({ pageNo: totalPages, pageSize })}
              disabled={currentPageNo >= totalPages}
            >
              {'>>'}
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default SubscriptionPlanMobileList;
